import { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import Cookies from 'js-cookie';
import metricsService from '../services/metrics';
import providersService from '../services/providers';
import useGandalfPostAuth from '../hooks/useGandalfPostAuth';
import TopHeader from '../components/TopHeader';
import Main from '../components/Main';
import ContainerLayout from '../components/ContainerLayout';
import Footer from '../components/Footer';
import { AppURL } from '../constants/urls';
import {
  IMPRESSIONS,
  LOGIN_ENTRY_METHOD_KEY,
  LOGIN_START_TIMESTAMP_KEY,
  SimplifiedLoginRumEvent,
} from '../constants/metrics';
import { redirectToLXPAuth, replayAuth } from '../services/auth';
import EulaCheck from '../components/postAuth/EulaCheck';
import AccountLinkConsent from '../components/postAuth/AccountLinkConsent';
import VerifyEmail from '../components/postAuth/VerifyEmail';
import LearnerConsentOnMerge3p from '../components/postAuth/LearnerConsentOnMerge3p';
import {
  GandalfUser,
  IdProvider,
  PostAuthComponentParams,
} from '../types/auth';
import { AppConfig } from '../types/app';
import SignInUnauthorized from '../components/SignInUnauthorized';
import SignInFailed from '../components/SignInFailed';
import UnauthorizedError from '../errors/UnauthorizedError';
import { IDP_URL_PARAM, URL_PARAMS } from '../constants/auth';
import MissingEmailError from '../errors/MissingEmailError';
import MissingEmail from '../components/MissingEmail';
import { useExplicitAmplifyConfig } from '../hooks/useAmplifyConfig';
import useIdpFromState from '../hooks/useIdpFromState';
import { rumService } from '../services/rum';
import { AccountLinkWarning } from '../components/postAuth/AccountLinkWarning';
import { GandalfSession } from '../components/postAuth/GandalfSession';
import LwaNewUserPrevention from '../components/postAuth/LwaNewUserPrevention';
import {
  IdPs,
  isEltiProvider,
  isStandardProvider,
} from '../constants/providers';
import React from 'react';
import IdentityConsent from '../components/postAuth/IdentityConsent';
import ProfileDeactivated from '../components/postAuth/ProfileDeactivated';
import SignInSwitchNeeded from '../components/postAuth/SignInSwitchNeeded';
import {
  GetPublicIdpDetailsOutput,
  queryApi,
} from '../hooks/useGetPublicIdpDetails';
import ProfileAndPreferences from '../components/postAuth/ProfileAndPreferences';
import { localStorageAdapter } from '../services/storage';
import SuspendedAccount from '../components/postAuth/SuspendedAccount';
import { PageTimeOutModal } from '../components/PageTimeOutModal';
import { AuthResponseSpinner } from '../components/AuthResponseSpinner';
import { getInitialLocaleSelection } from '../services/locale';
import AutoLinkConsent from '../components/postAuth/AutoLinkConsent';
import {
  AUTH_RETRY_ATTEMPTED_COOKIE,
  OTP_RETRY_COOKIE,
  OTP_RETRY_EXPIRY_MINUTES,
} from '../constants/cookies';

export enum PostAuthFlows {
  ProfileDeactivated = 'ProfileDeactivated',
  LwaNewUserPrevention = 'LwaNewUserPrevention',
  ProfileAndPreferences = 'ProfileAndPreferences',
  EULA = 'EULA',
  AccountLinkConsent = 'Account Link Consent',
  AccountLinkWarning = 'AccountLinkWarning',
  EmailVerification = 'Email Verification',
  LearnerConsentOnOrgInvite = 'Learner Consent on Org Invite',
  GandalfSession = 'Gandalf Session',
  LearnerConsentOnMerge3p = 'Learner Consent On Merge 3P',
  SignInSwitchNeeded = 'SignInSwitchNeeded',
  SuspendedAccount = 'SuspendAccountScreen',
  AutoLinkConsent = 'AutoLinkConsent',
}

export type PostAuthComponent = {
  type: PostAuthFlows;
  component: React.FC<PostAuthComponentParams>;
};

/**
 * List all post auth flow components in the order they should be displayed.
 */
export const postAuthFlowsNonElti: PostAuthComponent[] = [
  { type: PostAuthFlows.GandalfSession, component: GandalfSession },
  {
    type: PostAuthFlows.SuspendedAccount,
    component: SuspendedAccount,
  },
  { type: PostAuthFlows.ProfileDeactivated, component: ProfileDeactivated },
  { type: PostAuthFlows.SignInSwitchNeeded, component: SignInSwitchNeeded },
  { type: PostAuthFlows.LwaNewUserPrevention, component: LwaNewUserPrevention },
  { type: PostAuthFlows.EmailVerification, component: VerifyEmail },
  { type: PostAuthFlows.LearnerConsentOnOrgInvite, component: IdentityConsent },
  { type: PostAuthFlows.AccountLinkConsent, component: AccountLinkConsent },
  { type: PostAuthFlows.AutoLinkConsent, component: AutoLinkConsent },
  { type: PostAuthFlows.EULA, component: EulaCheck },
  { type: PostAuthFlows.AccountLinkWarning, component: AccountLinkWarning },
  {
    type: PostAuthFlows.LearnerConsentOnMerge3p,
    component: LearnerConsentOnMerge3p,
  },
  {
    type: PostAuthFlows.ProfileAndPreferences,
    component: ProfileAndPreferences,
  },
];

export const postAuthFlowsElti: PostAuthComponent[] = [
  { type: PostAuthFlows.GandalfSession, component: GandalfSession },
  { type: PostAuthFlows.EmailVerification, component: VerifyEmail },
];

const TOKEN_REFRESH_TIME_BUFFER_BEFORE_EXPIRY = 20 * 1000;

const getTimeToExpire = (tokenExpiration: number) =>
  tokenExpiration * 1000 - Date.now() - TOKEN_REFRESH_TIME_BUFFER_BEFORE_EXPIRY;

/**
 * Replaces current URL state to enable "back" and reload in the browser.
 */
function replaceUrlWithReloadSafeParams(searchParams: string) {
  const refreshParams = new URLSearchParams(searchParams);
  // Remove the identity_provider to not trigger preferred IDP flow.
  refreshParams.delete(IDP_URL_PARAM);
  window.history.replaceState(
    {},
    document.title,
    `${AppURL.Login}?${refreshParams.toString()}`
  );
}

function AuthResponse({ config }: { config: AppConfig }) {
  const [flowIndex, setFlowIndex] = useState(0);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [provider, setProvider] = useState<IdProvider | undefined>(undefined);
  const [consentAccepted, setConsentAccepted] = useState<boolean | undefined>(
    undefined
  );
  const [renderPageTimeOut, setRenderPageTimeOut] =
    React.useState<boolean>(false);
  const [idpDetails, setIdpDetails] = useState<
    GetPublicIdpDetailsOutput | undefined
  >(undefined);
  const [postAuthFlows, setPostAuthFlows] =
    useState<PostAuthComponent[]>(postAuthFlowsNonElti);
  const idpFromState = useIdpFromState();
  // Note: The first call to `Auth.configure` from this page will trigger a token exchange.  So, we have to ensure the
  // correct IDP configuration is provided.
  useExplicitAmplifyConfig(config, provider || idpFromState);
  const {
    user,
    error: postAuthError,
    customState,
    tokenExpirationTime,
  } = useGandalfPostAuth(false, config);
  const metricsPublisher = useRef(metricsService.getPublisher('AuthResponse'));
  const [authComponentError, setError] = useState(undefined);
  const error = postAuthError || authComponentError;
  const retryLock = useRef(false);
  const cookieCleared = useRef(false);

  useEffect(() => {
    metricsPublisher.current.publishCounterMonitor(IMPRESSIONS, 1);
  }, []);

  // Set RUM session attributes for user journey tracking via VibeId
  useEffect(() => {
    if (!user || !provider) return;
    const urlSearchParams = new URLSearchParams(window.location.search);
    rumService.addSessionAttributes({
      vibeID: user.vibeId,
      hatID: user.hatId,
      sourceIdP: user.providerName,
      clientId: urlSearchParams.get('client_id') || 'NA',
      currentPostAuthFlow: postAuthFlows[flowIndex]?.type || 'NA',
    });
  }, [user, provider, flowIndex, postAuthFlows]);

  // Clear retry cookies on successful auth
  useEffect(() => {
    if (user) {
      Cookies.remove(AUTH_RETRY_ATTEMPTED_COOKIE);
      Cookies.remove(OTP_RETRY_COOKIE);
    }
  }, [user]);

  useEffect(() => {
    const handleGlobalError = (event) => {
      if (
        event.error instanceof ReferenceError &&
        event.error.message.includes('process is not defined')
      ) {
        // eslint-disable-next-line no-console
        console.error(
          'Global handler caught "process is not defined" error:',
          event.error.message
        );
        // eslint-disable-next-line no-console
        console.error('Stack trace:', event.error.stack);
      }
    };

    // Add the event listener when the component mounts
    window.addEventListener('error', handleGlobalError);

    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener('error', handleGlobalError);
    };
  }, []);

  // handle token expire - use synchronous update to avoid React 18 batching issues
  useEffect(() => {
    if (!tokenExpirationTime) return;

    const timeToExpire = getTimeToExpire(tokenExpirationTime);
    // Don't set timeout if expiration is in the past
    if (timeToExpire <= 0) {
      flushSync(() => {
        setRenderPageTimeOut(true);
      });
      return;
    }

    const timeoutId = setTimeout(() => {
      flushSync(() => {
        setRenderPageTimeOut(true);
      });
    }, timeToExpire);

    // Clear timeout on unmount or when token changes
    return () => clearTimeout(timeoutId);
  }, [tokenExpirationTime]);

  // Await auth flow and custom OIDC state, the state contains the persistes search params.
  // When this is received we can start post auth flows and then continue redirecting to
  // the LXP specific auth.
  useEffect(() => {
    if (!customState) return;
    const idpConfig: IdProvider = JSON.parse(customState);
    const searchParams = new URL(idpConfig.url).search;

    const urlSearchParams = new URLSearchParams(searchParams);
    const idp = urlSearchParams.get(IDP_URL_PARAM);
    // Update the URL
    replaceUrlWithReloadSafeParams(searchParams);
    // Get the provider. Note, it will use values from the URL.
    const provider = providersService.transformNameToIdProvider(
      idp!,
      config.gandalfDomain
    );
    setProvider(provider);
  }, [config.gandalfDomain, customState]);

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    if (user?.new_user === true || user?.new_user === 'true') {
      rumService.recordEvent('vibe_created', {
        idp: user.providerName,
        clientId: urlSearchParams.get('client_id'),
        VibeId: user.vibeId,
        HatId: user.hatId,
        linkingCandidate: Boolean(user.associate_to),
        languageSelected: getInitialLocaleSelection(),
      });
    }
    if (user?.new_user === false || user?.new_user === 'false') {
      rumService.recordEvent('existing_user', {
        idp: user?.providerName,
        clientId: urlSearchParams.get('client_id'),
        VibeId: user?.vibeId,
        HatId: user?.hatId,
        linkingCandidate: Boolean(user?.associate_to),
        languageSelected: getInitialLocaleSelection(),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.new_user]);

  useEffect(() => {
    if (!config.enableIdpTypeCheck) return;
    if (!user) return;
    const checkIsELtiIdp = async (user: GandalfUser, config: AppConfig) => {
      const idpDetails = await queryApi(config, user.providerName);
      if (isEltiProvider(idpDetails?.idpType)) {
        const eltiFlows =
          config.enableSimplifiedSignIn === 'true'
            ? [
                ...postAuthFlowsElti,
                { type: PostAuthFlows.EULA, component: EulaCheck },
              ]
            : postAuthFlowsElti;
        setPostAuthFlows(eltiFlows);
      } else {
        setPostAuthFlows(postAuthFlowsNonElti);
      }
      setIdpDetails(idpDetails);
    };
    checkIsELtiIdp(user, config);
  }, [config, user]);

  const handleContinue = () => {
    const nextFlowIndex = flowIndex + 1;
    metricsPublisher.current.publishCounterMonitor('PostAuthContinue', 1);
    // Are we ready to redirect to LXP auth?
    if (nextFlowIndex === postAuthFlows.length) {
      // Emit login conversion and time-to-complete metrics
      const loginStartTs = sessionStorage.getItem(LOGIN_START_TIMESTAMP_KEY);
      if (loginStartTs) {
        const timeToComplete = Date.now() - parseInt(loginStartTs, 10);
        const entryMethod =
          sessionStorage.getItem(LOGIN_ENTRY_METHOD_KEY) || 'unknown';
        const isNewUser =
          user?.new_user === true || user?.new_user === 'true' ? 1 : 0;
        rumService.recordEvent(
          SimplifiedLoginRumEvent.LOGIN_CONVERSION_SUCCESS,
          {
            timeToCompleteMs: timeToComplete,
            idp: provider?.idp || 'unknown',
            entryMethod,
            isNewUser,
            clientId:
              new URLSearchParams(window.location.search).get('client_id') ||
              'NA',
          }
        );
        rumService.recordEvent(SimplifiedLoginRumEvent.LOGIN_TIME_TO_COMPLETE, {
          durationMs: timeToComplete,
          durationSec: Math.round(timeToComplete / 1000),
          idp: provider?.idp || 'unknown',
          entryMethod,
          isNewUser,
        });
        metricsPublisher.current.publishTimerMonitor(
          'LoginTimeToComplete',
          timeToComplete
        );
        sessionStorage.removeItem(LOGIN_START_TIMESTAMP_KEY);
        sessionStorage.removeItem(LOGIN_ENTRY_METHOD_KEY);
      }
      metricsPublisher.current.publishCounterMonitor(
        'LoginConversionSuccess',
        1
      );
      // Flush RUM events via beacon before page navigation
      rumService.dispatchBeacon();
      // Use sync update to ensure isRedirecting state is set before the redirect happens
      flushSync(() => {
        setIsRedirecting(true);
      });
      redirectToLXPAuth(
        config.enableGandalfSession === 'true' &&
          config.gandalfSessionEndpoint &&
          !isStandardProvider(provider!.idp)
          ? providersService.transformNameToIdProvider(
              IdPs.GandalfSession,
              config.gandalfDomain
            )
          : provider!,
        user ?? undefined
      );
    } else {
      setFlowIndex(nextFlowIndex);
    }
  };

  function getLogoutUrl(config: AppConfig) {
    const redirectUrl = `${window.location.origin}${AppURL.Login}`;
    return `https://${config.gandalfDomain}/logout?client_id=${
      config.authClientId
    }&logout_uri=${encodeURIComponent(redirectUrl)}&response_type=code`;
  }

  const handleCancel = () => {
    metricsPublisher.current.publishCounterMonitor('PostAuthCancel', 1);
    const logoutUrl = getLogoutUrl(config);
    const params = new URLSearchParams(window.location.search);
    localStorageAdapter.setItem(URL_PARAMS, params.toString());
    window.location.href = logoutUrl;
  };

  const handleSomethingWentWrongRetry = (error: Error, idpFromState: any) => {
    const normalizedMessage = error.message?.replace(/\+/g, ' ');
    const isTimeoutError = normalizedMessage?.includes('Something went wrong');
    const hasRetried = Cookies.get(AUTH_RETRY_ATTEMPTED_COOKIE) === 'true';

    // If retry is already in progress, show spinner
    if (retryLock.current) {
      return (
        <AuthResponseSpinner
          config={config}
          postAuthFlows={postAuthFlows}
          flowIndex={flowIndex}
        />
      );
    }

    // Only want to retry once on Something Went Wrong error
    if (
      isTimeoutError &&
      !hasRetried &&
      !cookieCleared.current &&
      idpFromState
    ) {
      retryLock.current = true;
      Cookies.set(AUTH_RETRY_ATTEMPTED_COOKIE, 'true', { expires: 1 / 24 });

      // Set cookie flag for OTP retry
      if (idpFromState && idpFromState.idp === IdPs.OTP) {
        Cookies.set(OTP_RETRY_COOKIE, 'true', {
          expires: OTP_RETRY_EXPIRY_MINUTES / (24 * 60),
        });
      }

      const retryEntryMethod =
        sessionStorage.getItem(LOGIN_ENTRY_METHOD_KEY) || 'unknown';
      rumService.recordEvent('auth_retry_attempted', {
        idp: idpFromState.idp,
        errorMessage: error.message,
        isSimplifiedLogin:
          retryEntryMethod !== 'classic' && retryEntryMethod !== 'unknown'
            ? 1
            : 0,
      });

      const urlParams = new URLSearchParams(new URL(idpFromState.url).search);
      replayAuth(idpFromState.idp, urlParams);

      return (
        <AuthResponseSpinner
          config={config}
          postAuthFlows={postAuthFlows}
          flowIndex={flowIndex}
        />
      );
    }

    return null; // No retry needed
  };

  const enableCookieComponent = Boolean(
    config?.enableCookieComponent === 'true'
  );
  const isAuthenticatedWithProvider = Boolean(user && provider);

  if (error) {
    // eslint-disable-next-line no-console
    console.error(`Error occurred during authN ${error}`);

    // Retry on Something Went Wrong error
    const retryResult = handleSomethingWentWrongRetry(error, idpFromState);
    if (retryResult) {
      return retryResult;
    }

    // Clear cookie only once per component instance
    if (!cookieCleared.current) {
      Cookies.remove(AUTH_RETRY_ATTEMPTED_COOKIE);
      cookieCleared.current = true;
      Cookies.remove(OTP_RETRY_COOKIE);
    }

    // Show appropriate error components
    if (error instanceof UnauthorizedError) {
      return (
        <SignInUnauthorized
          config={config}
          error={error}
          provider={provider!}
        />
      );
    } else if (error instanceof MissingEmailError) {
      return (
        <MissingEmail config={config} error={error} provider={provider!} />
      );
    }
    return <SignInFailed config={config} error={error} provider={provider!} />;
  }

  if (config.enableIdpTypeCheck === 'true') {
    if (!Boolean(idpDetails) || (!user && !error))
      return (
        <AuthResponseSpinner
          config={config}
          postAuthFlows={postAuthFlows}
          flowIndex={flowIndex}
        />
      );
  }

  if (isRedirecting)
    return (
      <AuthResponseSpinner
        config={config}
        postAuthFlows={postAuthFlows}
        flowIndex={flowIndex}
      />
    );
  if (!isAuthenticatedWithProvider)
    return (
      <AuthResponseSpinner
        config={config}
        postAuthFlows={postAuthFlows}
        flowIndex={flowIndex}
      />
    );

  const AuthFlowComponent = postAuthFlows[flowIndex].component;
  return (
    <>
      <TopHeader config={config} />
      <Main config={config}>
        <ContainerLayout currentPostAuthFlow={postAuthFlows[flowIndex].type}>
          {renderPageTimeOut ? (
            <PageTimeOutModal config={config} user={user!} />
          ) : (
            <AuthFlowComponent
              user={user!}
              onContinue={handleContinue}
              onCancel={handleCancel}
              config={config}
              error={error}
              setError={setError}
              consentAccepted={consentAccepted}
              setConsetAccepted={setConsentAccepted}
              idpDetails={idpDetails}
            />
          )}
        </ContainerLayout>
      </Main>
      <Footer enableCookieComponent={enableCookieComponent} />
    </>
  );
}

export default AuthResponse;
